<template>
  <div class="referral-page">
    <div class="referee-info">
      {{ $t(`${i18nPrefix}.label.refereeInfo`) }}
    </div>
    <ResumeForm
      class="resume-form"
      ref="form"
      :formLine="formLine"
      :data="data"
    >
      <template #resumeFile="{line}">
        <van-cell
          class="resume-file-container"
          :title-class="['title-class', line.required? 'required' : '']"
          :title="line.label"
        >
          <template #label>
            <UploadFile
              ref="uploadFile"
              :field="line"
              @uploadFile="uploadFile"
            />
          </template>
        </van-cell>
      </template>
    </ResumeForm>
    <div class="personal-info">
      <div class="personal-info-title">
        {{ $t(`${i18nPrefix}.label.personal`) }}
      </div>
      <ResumeForm
        class="resume-form"
        :formLine="personalLine"
        :data="accountInfo"
      >
        <template #name="{ data, line }">
          <van-row class="row"  :gutter="20">
            <van-col :span="12">
              <VanCustomField
                :field="line.firstName"
                :data="data"
              >
              </VanCustomField>
            </van-col>
            <van-col :span="12">
              <VanCustomField
                :field="line.lastName"
                :data="data"
              >
              </VanCustomField>
            </van-col>
          </van-row>
        </template>
      </ResumeForm>
    </div>
    <div class="policy">{{ $t(`${this.i18nPrefix}.tips.candidateSubmitPolicy`) }}</div>
    <AIResumeParseLoadingDialog :isShow="showAIResumeParseDialog" isMobile/>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { STEP_TYPE } from '@/constant/candidate'
import { mixedRouterPush } from '@/router/routes'
import ResumeForm from '@/components/mobile/resumeForm.vue'
import VanCustomField from '@/components/mobile/vanCustomField.vue'
import UploadFile from '@/components/mobile/uploadFile.vue'
import { validateEmailByVant } from '@/utils/validator'
import mixin from './mixin'
import { generateTempFileKey, getFileExtension, uploadFile } from '@/utils/oss'
import AIResumeParseLoadingDialog from '@/components/dialog/aiResumeParseLoadingDialog.vue'
import { MAX_UPLOAD_RESUME_FILE_SIZE } from '@/constant/application'

export default {
  mixins: [mixin],
  components: {
    AIResumeParseLoadingDialog,
    ResumeForm,
    UploadFile,
    VanCustomField
  },
  props: {
    resumeEditorSetting: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'personal.resume',
      data: {
        resumeFile: null,
        emailAddress: null,
        phoneNumber: null,
        firstName: null,
        lastName: null,
        currentCompany: null,
        currentPosition: null,
        referralReason: null
      },
      accountInfo: {},
      currenUser: {
        emailAddress: null,
        firstName: null,
        lastName: null
      },
      acceptFileTypes: [
        'text/html',
        'application/pdf',
        'application/msword', // .doc
        'application/wps-office.doc',
        'application/wps-office.docx',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/bmp'
      ],
      formErrors: {
        email: {
          checked: null,
          error: null
        },
        mobile: {
          checked: null,
          error: null
        }
      },
      showAIResumeParseDialog: false
    }
  },
  computed: {
    ...mapGetters('company', ['companyThemeSetting']),
    jobId () {
      return this.$route.query?.job_id
    },
    companyId () {
      const companyId = this.$route.query?.company_id
      return companyId && _.toNumber(companyId)
    },
    setting () {
      return this.resumeEditorSetting?.[STEP_TYPE.SUBMIT_REFERRAL] || {}
    },
    needUploadResume () {
      return this.setting?.needUploadResume || false
    },
    noNeedReason () {
      return this.setting?.noNeedReason || false
    },
    formLine () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = [
        {
          name: 'emailAddress',
          label: this.$t(`${this.i18nPrefix}.label.emailAddress`),
          rules: [{ required: true, message: requiredMessage, trigger: 'onBlur' },
            { validator: this.validateEmailAddress, message: this.$t('common.message.invalidEmail'), trigger: 'onBlur' }
          ]
        },
        {
          name: 'phoneNumber',
          label: this.$t(`${this.i18nPrefix}.label.phoneNumber`),
          rules: [
            { required: true, message: requiredMessage, trigger: 'onBlur' },
            { validator: this.validateMobile, message: this.$t('common.message.invalidMobile'), trigger: 'onBlur' }
          ]
        },
        {
          name: 'firstName',
          label: this.$t(`${this.i18nPrefix}.label.firstName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'onBlur' }]
        },
        {
          name: 'lastName',
          label: this.$t(`${this.i18nPrefix}.label.lastName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'onBlur' }]
        },
        {
          name: 'currentCompany',
          label: this.$t(`${this.i18nPrefix}.label.currentCompany`)
        },
        {
          name: 'currentPosition',
          label: this.$t(`${this.i18nPrefix}.label.currentPosition`)
        },
        {
          name: 'referralReason',
          elType: 'text',
          maxLength: 2000,
          label: this.$t(`${this.i18nPrefix}.label.referralReason`),
          rules: [{ required: this.noNeedReason, message: requiredMessage, trigger: 'onBlur' }]
        },
        {
          slotName: 'resumeFile',
          maxSize: 1,
          noDescription: true,
          fileType: this.acceptFileTypes,
          preview: false,
          uploaderFileSize: MAX_UPLOAD_RESUME_FILE_SIZE,
          fileSizeError: this.fileSizeError,
          required: this.needUploadResume,
          btnName: this.$t(`${this.i18nPrefix}.label.uploadResume`),
          label: this.$t(`${this.i18nPrefix}.label.uploadResume`)
        }
      ]
      return lines
    },
    personalLine () {
      return [
        {
          name: 'email_address',
          disabled: true,
          label: this.$t(`${this.i18nPrefix}.label.emailAddress`),
          placeholder: ''
        },
        {
          slotName: 'name',
          firstName: {
            name: 'first_name',
            disabled: true,
            label: this.$t(`${this.i18nPrefix}.label.firstName`),
            placeholder: ''
          },
          lastName: {
            name: 'last_name',
            disabled: true,
            label: this.$t(`${this.i18nPrefix}.label.lastName`),
            placeholder: ''
          }
        }
      ]
    },
    useAIResumeParse () {
      return this.companyThemeSetting?.company.has_ai_resume_parse_premium
    }
  },
  async created () {
    this.accountInfo = this.getCurrentAccount()
    if (!this.jobId) {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(backUrl)
      } else if (this.jobId) {
        this.$router.push(`/job-detail-new/${this.jobId}/c/`)
      } else {
        this.$router.push({ path: '/recruiting/personal/home', query: { company_id: this.companyId } })
      }
    } else {
      this.$eventBus.$emit('showLoading', true)
      try {
        await Promise.all([
          this.initPersonalInfo(),
          this.initJobRequiredInfo()
        ])
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    }
  },
  mounted () {},
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('personal', ['submitResumeToReferral', 'getPersonalInfo']),
    ...mapActions('application', ['getJobApplyRequiredInfo']),
    ...mapActions('referral', ['getReferralUploadedCandidateCount']),
    async handleCancel () {
      let backUrl = this.$route.query.back
      backUrl = _.replace(backUrl, 'go_to_submit=1', 'go_to_submit=0')
      if (backUrl) {
        mixedRouterPush(backUrl)
      } else if (this.jobId) {
        this.$router.push(`/job-detail-new/${this.jobId}/c/`)
      } else {
        this.$router.push({ path: '/recruiting/personal/home', query: { company_id: this.companyId } })
      }
    },
    async initPersonalInfo () {
      const resp = await this.getPersonalInfo()
      if (resp) {
        this.accountInfo.email_address = resp.email_address
      }
    },
    async initJobRequiredInfo () {
      const resp = await this.getJobApplyRequiredInfo(this.jobId)
      if (resp) {
        if ((resp.required_field || []).includes('resume')) {
          this.setting.needUploadResume = true
        }
      }
    },
    async getFormEl () {
      let formEl = this.$refs.form
      if (!formEl) {
        await this.$nextTick()
      }
      formEl = this.$refs.form
      return formEl
    },
    uploadFile (fileInfo, callback) {
      this.data.resumeFile = fileInfo.file
      const success = true
      callback(success)
    },
    fileSizeError () {
      this.$toast({ message: this.$t('admin.application.candidate.message.uploadTips') })
    },
    async submit (candidateInfo, autoBack) {
      const form = await this.getFormEl()
      if (!form) {
        return
      }
      let valid = await form.submit()
      if (valid) {
        valid = this.$refs.uploadFile.validate()
      }
      if (!valid) {
        return
      }
      const referralInfo = this.data
      if (this.useAIResumeParse) {
        this.showAIResumeParseDialog = true
      } else {
        this.$eventBus.$emit('showLoading', true)
      }
      try {
        const resumeFile = referralInfo.resumeFile
        let fileKey
        if (resumeFile) {
          const fileExt = getFileExtension(resumeFile)
          fileKey = generateTempFileKey(fileExt)
          await uploadFile(resumeFile, fileKey)
        }
        const params = {
          phone: referralInfo.phoneNumber,
          submit_file_name: resumeFile?.name,
          email_address: referralInfo.emailAddress,
          current_company: referralInfo.currentCompany,
          current_position: referralInfo.currentPosition,
          referral_reason: referralInfo.referralReason,
          first_name: referralInfo.firstName,
          last_name: referralInfo.lastName,
          resume_file: fileKey,
          referral_account: this.accountInfo.id,
          job_id: this.jobId
        }
        const resp = await this.submitResumeToReferral(params)
        if (resp && resp.status === 'success') {
          mixedRouterPush(`/mobile-referral-submit-success/${this.jobId}`)
        } else if (_.includes([20006042], resp.code)) {
          this.$toast({ message: this.$t(`error.code.${resp.code}`) })
        } else {
          this.$toast({ message: this.$t(`${this.i18nPrefix}.tips.submitFailed`) })
        }
      } catch (error) {
        this.$toast({ message: this.$t(`${this.i18nPrefix}.tips.submitFailed`) })
      } finally {
        if (this.useAIResumeParse) {
          this.showAIResumeParseDialog = false
        } else {
          this.$eventBus.$emit('showLoading', false)
        }
      }
    },
    async hasInTalentPool (email, phone) {
      if (!email && !phone) {
        return
      }
      const params = {
        email: email,
        phone: phone,
        job_id: this.$route.query.job_id
      }
      const uploadedCount = await this.getReferralUploadedCandidateCount(params)
      return uploadedCount && uploadedCount > 0
    },
    async validateEmailAddress (value, rule) {
      let errMsg = null
      const formError = this.formErrors.email
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          errMsg = formError.error
        }
      } else {
        this.changeLoading(true)
        try {
          errMsg = validateEmailByVant(value)
          if (!errMsg) {
            const email = value
            const hasInTalentPool = await this.hasInTalentPool(email, null)
            if (hasInTalentPool) {
              errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
            }
          }
        } finally {
          formError.checked = value
          formError.error = errMsg
          this.changeLoading(false)
        }
      }
      if (errMsg) {
        rule.message = errMsg
        return false
      } else {
        return true
      }
    },
    async validateMobile (value, rule) {
      let errMsg = null
      const formError = this.formErrors.mobile
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          errMsg = formError.error
        }
      } else {
        this.changeLoading(true)
        try {
          // # 7509 移除手机号校验
          // errMsg = validatePhoneByVant(value)
          const phone = value
          const hasInTalentPool = await this.hasInTalentPool(null, phone)
          if (hasInTalentPool) {
            errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
          }
        } finally {
          formError.checked = value
          formError.error = errMsg
          this.changeLoading(false)
        }
      }
      if (errMsg) {
        rule.message = errMsg
        return false
      } else {
        return true
      }
    },
    changeLoading (value) {
      this.$emit('load', value)
    },
    fileToBase64 (file) {
      if (!file) {
        return ''
      }
      const reader = new FileReader()
      // 将文件加载进入
      reader.readAsDataURL(file)
      return new Promise((resolve, reject) => {
        reader.onload = function () {
          resolve(this.result)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.referral-page {
  padding-top: 10px;

  .resume-form {
    padding: 14px 18px;
  }

  .referee-info {
    margin-bottom: 10px;
    font-size: 16px;
    color: #606266;
    text-align: center;
  }

  .resume-file-container {
    padding: 0;

    .title-class {
      font-weight: 400;
      font-size: 12px;
      color: #909399;
      line-height: 16px;
      margin-bottom: 5px;

      .van-cell__label {
        margin-top: 10px;
      }
    }

    .required {

      span {

        &::after {
          position: relative;
          left: 2px;
          color: #ee0a24;
          font-size: 14px;
          content: '*';
        }

      }
    }
  }

  .personal-info {

    .personal-info-title {
      margin: 10px 16px 0;
      padding: 10px 0;
      text-align: center;
      font-size: 16px;
      color: #606266;
      border-top: 1px solid #DCDFE6;
    }
  }

  .policy {
    padding: 0 18px;
    margin: 6px 0 14px;
    font-size: 10px;
    color: #909399;
  }
}
</style>
